import React, { useState } from 'react';
import { cn } from '../../utils/cn';

// keep your SVGs exactly as provided
const PrevSvg = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M6.5 11L1.5 6M1.5 6L6.5 1M1.5 6H16.5"
			stroke="#180A63"
			strokeWidth="1.875"
			strokeMiterlimit="10"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const NextSvg = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M11.5 11L16.5 6M16.5 6L11.5 1M16.5 6H1.5"
			stroke="#180A63"
			strokeWidth="1.875"
			strokeMiterlimit="10"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const Pagination = ({ totalDocs, docsPerPage = 8, onPageChange }) => {
	const [currentPage, setCurrentPage] = useState(1);
	const totalPages = Math.max(1, Math.ceil(totalDocs / docsPerPage));

	const handlePageClick = (page) => {
		if (page >= 1 && page <= totalPages) {
			setCurrentPage(page);
			onPageChange?.(page);
		}
	};

	// build range like: 1 2 3 … 8 9 10 (current boxed)
	const renderPageNumbers = () => {
		const pages = [];
		const windowSize = 1; // show current-1, current, current+1

		for (let i = 1; i <= totalPages; i++) {
			if (
				i === 1 ||
				i === totalPages ||
				(i >= currentPage - windowSize && i <= currentPage + windowSize)
			) {
				if (pages.length && i - pages[pages.length - 1] > 1) pages.push('…');
				pages.push(i);
			}
		}

		return pages.map((p, idx) =>
			p === '…' ? (
				<span key={`ellipsis-${idx}`} className="px-2 text-dark-blue">
					…
				</span>
			) : (
				<button
					key={`page-${p}`}
					onClick={() => handlePageClick(p)}
					className={cn(
						'px-3 py-3 text-base rounded-md font-medium',
						p === currentPage ? 'bg-white text-dark-blue border border-[#F0F0F0]' : 'text-dark-blue'
					)}
					aria-current={p === currentPage ? 'page' : undefined}
				>
					{p}
				</button>
			)
		);
	};

	if (totalPages <= 1) return null;

	return (
		<nav className="grid grid-cols-3 items-center py-6" aria-label="Pagination">
			{/* Previous (left) */}
			<div className="justify-self-start items-center">
				<button
					onClick={() => handlePageClick(currentPage - 1)}
					disabled={currentPage === 1}
					className={cn(
						'flex items-center gap-x-2 rounded-lg bg-white border border-[#F0F0F0] px-3 py-3 text-base font-medium text-dark-blue transition',
						currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'
					)}
					aria-label="Previous page"
				>
					<PrevSvg />
					<span className="flex items-center">Previous</span>
				</button>
			</div>

			{/* Numbers (center) */}
			<div className="justify-self-center items-center">
				<div className="flex items-center gap-2">{renderPageNumbers()}</div>
			</div>

			{/* Next (right) */}
			<div className="justify-self-end items-center">
				<button
					onClick={() => handlePageClick(currentPage + 1)}
					disabled={currentPage === totalPages}
					className={cn(
						'flex items-center rounded-lg gap-x-2 bg-white border border-[#F0F0F0] p-3 text-base font-medium text-dark-blue transition',
						currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'
					)}
					aria-label="Next page"
				>
					<span className="flex items-center">Next</span>
					<NextSvg />
				</button>
			</div>
		</nav>
	);
};

export default Pagination;
